import { find, isEmpty } from 'lodash'
import customFilterResource from '@/api/resources/ui/CustomFilterResource'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import sideBarOptions from './sideBarOptions'
import ExpandedSideBar from '@/components/sideBar/ExpandedSideBar'
import CompactSideBar from '@/components/sideBar/CompactSideBar'
import { CASE_SITUATIONS } from '@/modules/diagnosisModule/services/caseSituations'
import { HelperUtils } from '@/shared/util/helper-utils'
import { CaseOperation } from '@/api/operations/models/case-operation'

class DiagnosisSideBarStore {
    constructor() {
        this.sideBars = [ExpandedSideBar, CompactSideBar]
        this.loadedCustomOptions = false
        this.loadedConfig = false
        this.firstLevel = sideBarOptions.expanded.reduce((prev, next) => next.children ? prev.concat(next.children) : prev.concat(next), [])
        this.completeArray = this.firstLevel.reduce((prev, next) => next && next.children ? prev.concat(next.children) : prev.concat(next), [])
        this.defaultOption = this.completeArray.find(obj => obj.id === CASE_SITUATIONS.PENDING_GROSSING)
        this.state = {
            externalFilterModel: {},
            customFilterModel: {},
            filterModel: this.defaultOption.filterModel,
            componentCode: 'uicomponent.diagnosis.casepanel',
            options: sideBarOptions,
            sideBar: ExpandedSideBar,
            fallbackOption: this.defaultOption,
            selectedOption: this.defaultOption,
            sideBarName: sideBarOptions.sideBarName,
            gridRouteName: 'diagnosisGrid',
            customOptions: [],
            config: this.getDefaultConfig(),
            checkboxesConfig: null
        }
    }

    getDefaultConfig() {
        return {
            defaultOptionId: null,
            sideBarName: ExpandedSideBar.name,
            viewAllEnabled: false,
            responsibility: undefined
        }
    }

    setViewAll(viewAllEnabled) {
        this.state.config.viewAllEnabled = viewAllEnabled
    }
    setResponsibility(responsibility) {
        this.state.config.responsibility = responsibility
    }

    hasLoaded() {
        return this.loadedCustomOptions && this.loadedConfig
    }

    async expandSideBar() {
        this.state.sideBar = ExpandedSideBar
        await this.saveConfig()
    }

    async contractSideBar() {
        this.state.sideBar = CompactSideBar
        await this.saveConfig()
    }

    contractSideBarLocally() {
        this.state.sideBar = CompactSideBar
    }

    selectDefaultOption() {
        const option = this.findOptionById(this.state.config.defaultOptionId)

        if (!option) {
            console.warn(`customOption with id ${this.state.config.defaultOptionId} does not exist, selecting fallback option`)
            this.selectFallbackOption()
            return
        }
        this.setSelectedOption(option)
    }

    selectFallbackOption() {
        this.setSelectedOption(this.state.fallbackOption)
    }

    findOptionById(id) {
        const predicate = o => o.id === id
        return find(this.completeArray, predicate) || find(this.state.customOptions, predicate)
    }

    setSelectedOption(option) {
        this.state.selectedOption = option
        if (option.filterModel && option.customFilterModel && !isEmpty(option.customFilterModel)) {
            this.setFilterModel(option.filterModel)
        } else if (option.filterModel) {
            this.setFilterModel(option.filterModel)
        } else if (option.customFilterModel) {
            this.setFilterModel(option.customFilterModel)
        } else {
            this.setFilterModel()
        }
        this.state.selectedOption.caseSituation = option.caseSituation ?? null
    }

    setFilterModel(checkFilterModel) {
        this.state.filterModel = null
        if (checkFilterModel) {
            if (this.state.selectedOption.filterModel) {
                this.state.filterModel = Object.assign(this.state.selectedOption.filterModel, checkFilterModel)
            } else {
                this.state.filterModel = checkFilterModel
            }
        } else {
            this.state.filterModel = this.state.selectedOption.filterModel
        }
    }

    setDefaultOption(option) {
        this.state.config.defaultOptionId = option && option.id
    }

    async loadCustomOptions() {
        const customOptions = await customFilterResource.indexAsOptions(this.state.componentCode)
        this.setCustomOptions(customOptions)
        this.loadedCustomOptions = true
    }

    setCustomOptions(customOptions) {
        this.state.customOptions = customOptions
    }

    async removeCustomOption(option) {
        const index = this.state.customOptions.findIndex(i => i.id === option.id)
        if (index === -1) {
            throw new Error(`custom option with id ${option.id} does not exist`)
        }
        await customFilterResource.deleteCustomOption(this.state.componentCode, option)
        if (option.id === this.state.selectedOption.id) {
            this.setSelectedOption(this.defaultOption)
        }
        this.state.customOptions.splice(index, 1)
    }

    async renameCustomOption(option) {
        await customFilterResource.renameCustomOption(this.state.componentCode, option)
        await this.loadCustomOptions()
        this.updateSelectedOption()
    }
    async updateCustomOption(option, filterModel) {
        return customFilterResource.updateCustomOption(this.state.componentCode, option, filterModel)
    }
    updateSelectedOption() {
        const customOptionSelectedOption = this.state.customOptions.find(customOption => customOption.id === this.state.selectedOption?.id)
        if (customOptionSelectedOption) {
            this.setSelectedOption(customOptionSelectedOption)
        }
    }
    _viewAllAllowed() {
        return HelperUtils.hasOperationInCurrentLaboratory(CaseOperation.VIEW_ALL_DIAGNOSE)
    }
    _applyConfigSideBar() {
        const sideBar = this.sideBars.find(sidebar => sidebar.name === this.state.config.sideBarName)
        if (!sideBar) {
            throw new Error(`sideBar named ${this.state.config.sideBarName} not found`)
        }
        this.state.sideBar = sideBar
    }
    _setConfig(config) {
        this.state.config = config
        this._applyConfigSideBar()
    }
    async loadConfig() {
        let config = this.getDefaultConfig()
        const customConfig = await customConfigResource.get(this.state.componentCode)
        if (customConfig) {
            config = {
                ...config,
                ...customConfig.config,
                viewAllEnabled: this._viewAllAllowed() ? customConfig.config.viewAllEnabled : false
            }
        }
        this._setConfig(config)
        this.loadedConfig = true
    }

    async saveConfig() {
        const config = {
            ...this.state.config,
            ...this.state.checkboxesConfig,
            sideBarName: this.getCurrentSideBarName()
        }
        await customConfigResource.save(this.state.componentCode, config)
        this.state.config = config
    }

    getCurrentSideBarName() {
        if (!this.state.sideBar) {
            throw new Error('state.sideBar is not set')
        }
        return this.state.sideBar.name
    }
}

export default new DiagnosisSideBarStore()
